<div class="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
    <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
            <h2 class="text-2xl font-bold tracking-tight text-primary">Results</h2>
            <p class="mt-1 max-w-prose text-sm text-secondary">Posts this brief has stats for. Stories stay on Content.</p>
        </div>
        @if (scan().status === 'list') {
        <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary shrink-0" (click)="exportResults()">
            Export CSV
        </button>
        }
    </div>

    @if (loading()) {
    <div aria-busy="true" aria-label="Loading results">
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
            @for (cell of skeletonTotals; track cell) {
            <div class="h-16 animate-pulse rounded-xl bg-muted"></div>
            }
        </div>
        <div class="mt-4 space-y-2">
            @for (row of skeletonRows; track row) {
            <div class="h-12 animate-pulse rounded-lg bg-muted"></div>
            }
        </div>
    </div>
    } @else if (scan().status === 'empty') {
    <div class="rounded-xl border border-dashed border-border p-10 text-center">
        <h3 class="text-sm font-semibold text-primary">No posts found yet</h3>
    </div>
    } @else {
    <div class="flex flex-wrap items-end gap-3">
        <label class="grid min-w-48 flex-1 gap-1 text-xs font-medium text-secondary">
            Search
            <input [value]="searchDraft()" (input)="onSearch($event)" type="search" aria-label="Search results"
                placeholder="Name, handle, or caption" class="rounded-lg border border-border bg-card px-3 py-2 text-sm text-primary" />
        </label>
        <label class="grid gap-1 text-xs font-medium text-secondary">
            Creator
            <select aria-label="Creator" [value]="query().creatorId" (change)="onCreator($event)"
                class="rounded-lg border border-border bg-card px-3 py-2 text-sm text-primary">
                <option value="">All creators</option>
                @for (creator of scan().creators; track creator.id) {
                <option [value]="creator.id" [disabled]="creator.disabled">{{ creator.name }}</option>
                }
            </select>
        </label>
        <label class="grid gap-1 text-xs font-medium text-secondary">
            Sort
            <select aria-label="Sort" [value]="query().sort" (change)="onSort($event)"
                class="rounded-lg border border-border bg-card px-3 py-2 text-sm text-primary">
                @for (option of sortOptions; track option.id) {
                <option [value]="option.id">{{ option.label }}</option>
                }
            </select>
        </label>
        <label class="grid gap-1 text-xs font-medium text-secondary">
            Direction
            <select aria-label="Direction" [value]="query().direction" (change)="onDirection($event)"
                class="rounded-lg border border-border bg-card px-3 py-2 text-sm text-primary">
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
            </select>
        </label>
    </div>

    <dl class="m-0 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Posts</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.posts }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Views</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.views }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Likes</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.likes }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Comments</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.comments }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Shares</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.shares }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Saves</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ scan().totals.saves }}</dd>
        </div>
        <div class="rounded-xl border border-border bg-card px-4 py-3">
            <dt class="text-xs font-bold uppercase tracking-wide text-secondary">Engagement rate</dt>
            <dd class="m-0 mt-1 text-xl font-bold text-primary">{{ rateText(scan().totals.averageEngagementRate) }}</dd>
        </div>
    </dl>

    @if (scan().status === 'no-match') {
    <div class="rounded-xl border border-dashed border-border p-10 text-center">
        <h3 class="text-sm font-semibold text-primary">No posts match</h3>
        <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary mt-3" (click)="clearFilters()">
            Clear filters
        </button>
    </div>
    } @else {
    <div class="min-h-0 flex-1 overflow-auto rounded-xl border border-border bg-card shadow-sm">
        <table class="w-full min-w-full text-left text-sm">
            <thead class="sticky top-0 bg-muted text-xs uppercase tracking-wide text-secondary">
                <tr>
                    <th scope="col" class="px-3 py-3 font-bold">Creator</th>
                    <th scope="col" class="px-3 py-3 font-bold">
                        <p class="m-0">Followers</p>
                        <p class="m-0 text-xs font-normal normal-case tracking-normal">Current account size</p>
                    </th>
                    <th scope="col" class="px-3 py-3 font-bold">Posted</th>
                    <th scope="col" class="px-3 py-3 font-bold">Views</th>
                    <th scope="col" class="px-3 py-3 font-bold">Likes</th>
                    <th scope="col" class="px-3 py-3 font-bold">Comments</th>
                    <th scope="col" class="px-3 py-3 font-bold">Shares</th>
                    <th scope="col" class="px-3 py-3 font-bold">Saves</th>
                    <th scope="col" class="px-3 py-3 font-bold">Engagement</th>
                    <th scope="col" class="px-3 py-3 font-bold">
                        <p class="m-0">Engagement rate</p>
                        <p class="m-0 text-xs font-normal normal-case tracking-normal">Engagement / views</p>
                    </th>
                </tr>
            </thead>
            <tbody>
                @for (row of scan().rows; track row.socialPostId) {
                <tr class="cursor-pointer border-t border-border" (click)="openPost(row.socialPostId)">
                    <td class="px-3 py-3">
                        <div class="flex items-center gap-3">
                            @if (row.coverUrl) {
                            <img [src]="row.coverUrl" alt="" class="size-10 shrink-0 rounded-md object-cover" />
                            }
                            <div class="min-w-0 flex-1">
                                <p class="m-0 truncate font-medium text-primary">{{ row.creatorName || row.handle }}</p>
                                <p class="m-0 truncate text-xs text-secondary">{{ row.handle }}</p>
                            </div>
                            <button type="button" class="btn-shout btn-shout-xs btn-shout-tertiary shrink-0"
                                (click)="openPost(row.socialPostId)">Open</button>
                        </div>
                    </td>
                    <td class="px-3 py-3 text-primary">{{ row.followers }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.postedLabel }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.views }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.likes }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.comments }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.shares }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.saves }}</td>
                    <td class="px-3 py-3 text-primary">{{ row.engagement }}</td>
                    <td class="px-3 py-3 text-primary">{{ rateText(row.engagementRate) }}</td>
                </tr>
                }
            </tbody>
        </table>
    </div>
    @if (scan().pageCount > 1) {
    <div class="flex items-center justify-between gap-3">
        <p class="m-0 text-sm text-secondary">Page {{ scan().page }} of {{ scan().pageCount }}</p>
        <div class="flex gap-2">
            <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary" [disabled]="scan().page <= 1"
                (click)="showPage(scan().page - 1)">Previous</button>
            <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary" [disabled]="scan().page >= scan().pageCount"
                (click)="showPage(scan().page + 1)">Next</button>
        </div>
    </div>
    }
    }
    }
</div>

<mbtm-mon-post-drawer [posts]="scan().matched" [(openedPostId)]="openedPostId" />
